import { useState, useEffect } from 'react';
import { Sparkles, X, Quote } from 'lucide-react';
import { CreatureAvatar } from '../creatures/CreaturePanel';

interface EvolutionModalProps {
  dominantCreature: string | null;
  onClose: () => void;
}

const INTELLIGENT_LIFE_LORE: Record<string, {
  title: string;
  trait: string;
  originStory: string;
  uniqueAbility: string;
  civilisationBonus: string;
  flavourQuote: string;
  gradient: string;
  accent: string;
  badgeBg: string;
}> = {
  blob: {
    title: 'The Amoeboid Collective',
    trait: 'Cooperative',
    originStory: 'What began as solitary gelatinous drifters in warm tidal pools became something extraordinary. Over millennia, their cells learned to merge — not just physically, but mentally. Individual consciousness dissolved into a vast, shared mind where every blob knows what every other blob knows.',
    uniqueAbility: 'Cellular Memory — knowledge and skills spread instantly across the entire collective. When one blob learns, all blobs know.',
    civilisationBonus: '+15% population growth from shared labour networks',
    flavourQuote: '"We are not many. We are one, wearing many faces."',
    gradient: 'from-pink-950/80 to-slate-900',
    accent: 'text-pink-300',
    badgeBg: 'bg-pink-900/40 border-pink-700/40',
  },
  squid: {
    title: 'The Cephalopod Scholars',
    trait: 'Intelligent',
    originStory: 'Deep in the ocean trenches, where light never reaches, the squids developed problem-solving to navigate absolute darkness. Their tentacles became tools, their ink became writing, and their three hearts beat with insatiable curiosity that eventually drove them to the surface.',
    uniqueAbility: 'Distributed Cognition — each tentacle operates semi-independently, allowing parallel thought and multitasking beyond any other species.',
    civilisationBonus: '+20% technology generation from innate curiosity',
    flavourQuote: '"Every question is a door. We intend to open them all."',
    gradient: 'from-purple-950/80 to-slate-900',
    accent: 'text-purple-300',
    badgeBg: 'bg-purple-900/40 border-purple-700/40',
  },
  shell: {
    title: 'The Shell-Armoured Architects',
    trait: 'Defensive',
    originStory: 'Born on mineral-rich shores where predators lurked in every shadow, the shell-folk learned that the best defence is not just a strong shell — it is a strong wall around everything you love. Their first cities were fortresses before they were homes.',
    uniqueAbility: 'Calcified Memory — their shells record environmental data in growth rings, giving them perfect historical recall of climate, events, and threats.',
    civilisationBonus: '+25% stability from fortified structures',
    flavourQuote: '"A home without walls is just a waiting place for trouble."',
    gradient: 'from-amber-950/80 to-slate-900',
    accent: 'text-amber-300',
    badgeBg: 'bg-amber-900/40 border-amber-700/40',
  },
  wing: {
    title: 'The Avian Sky-City Builders',
    trait: 'Aerial',
    originStory: 'Thermal currents were their first teachers. The wing-folk learned that height brings perspective — literally and figuratively. Their settlements grew upward instead of outward, connected by bridges of woven vine and wind. From the sky, they saw patterns the ground-dwellers never could.',
    uniqueAbility: 'Aerial Cartography — they map entire regions from flight, revealing resource locations and terrain advantages invisible from the ground.',
    civilisationBonus: '+15% research from aerial observation posts',
    flavourQuote: '"The ground is just a place to rest between journeys upward."',
    gradient: 'from-sky-950/80 to-slate-900',
    accent: 'text-sky-300',
    badgeBg: 'bg-sky-900/40 border-sky-700/40',
  },
  spike: {
    title: 'The Spiked Warrior Clan',
    trait: 'Resilient',
    originStory: 'Evolved in volcanic badlands where the ground itself is hostile, the spike-beasts are survivors of impossible odds. Every adaptation they developed — armoured plating, toxin resistance, extreme temperature tolerance — was forged in catastrophe. They do not merely endure hardship; they grow stronger from it.',
    uniqueAbility: 'Adaptive Armour — their spines and plating shift composition based on environmental threats, becoming fire-resistant, acid-proof, or impact-absorbing as needed.',
    civilisationBonus: '+20% biomass preservation during catastrophes',
    flavourQuote: '"The world tried to kill us a thousand times. We are still here."',
    gradient: 'from-red-950/80 to-slate-900',
    accent: 'text-red-300',
    badgeBg: 'bg-red-900/40 border-red-700/40',
  },
  fuzz: {
    title: 'The Fuzzy Tribal Network',
    trait: 'Social',
    originStory: 'Covered in dense insulating fur that lets them survive temperature swings, the fuzzlings discovered that warmth is better shared. Their villages became extended families where every member knows every other member by scent, voice, and touch. Trust is their currency.',
    uniqueAbility: 'Empathic Bonding — they can sense the emotional state of nearby kin, allowing rapid coordination and conflict resolution without violence.',
    civilisationBonus: '+10% happiness bonus to all colonies',
    flavourQuote: '"A warm den and trusted kin — what more does any heart need?"',
    gradient: 'from-emerald-950/80 to-slate-900',
    accent: 'text-emerald-300',
    badgeBg: 'bg-emerald-900/40 border-emerald-700/40',
  },
  crystal: {
    title: 'The Crystalline Chorus',
    trait: 'Harmonic',
    originStory: 'Not carbon-based but silicon-grown, the crystal-forms emerged in geothermal caverns where minerals precipitated into living lattices. They communicate through resonant frequencies — their entire civilisation is a symphony of vibrations passing through stone and crystal.',
    uniqueAbility: 'Resonant Computing — their crystal brains process information through harmonic interference patterns, giving them innate mathematical and logical brilliance.',
    civilisationBonus: '+15% stability and research from harmonic infrastructure',
    flavourQuote: '"Listen to the stones sing. That is our voice."',
    gradient: 'from-cyan-950/80 to-slate-900',
    accent: 'text-cyan-300',
    badgeBg: 'bg-cyan-900/40 border-cyan-700/40',
  },
  float: {
    title: 'The Cloud City Dwellers',
    trait: 'Aerial',
    originStory: 'Jellyfish-like organisms that drifted into the upper atmosphere, the float-folk discovered that the sky is not empty — it is a vast, three-dimensional ocean of wind and moisture. They harvest water vapour and solar energy, building floating colonies that migrate with weather patterns.',
    uniqueAbility: 'Atmospheric Farming — they cultivate microbial colonies on their gas-filled membranes, producing nutrients and biomaterials from thin air.',
    civilisationBonus: '+10% atmosphere and research from cloud observatories',
    flavourQuote: '"The ground is a story we have outgrown."',
    gradient: 'from-indigo-950/80 to-slate-900',
    accent: 'text-indigo-300',
    badgeBg: 'bg-indigo-900/40 border-indigo-700/40',
  },
  burrow: {
    title: 'The Tunnel Republic',
    trait: 'Industrious',
    originStory: 'Subterranean by nature, the tunnel-worms reshaped the soil as they expanded their networks. What began as simple burrows became vast underground cities with ventilation shafts, water channels, and storage vaults. They understand the planet from the inside out.',
    uniqueAbility: 'Seismic Sense — their bodies detect vibrations through the ground, giving them early warning of earthquakes, approaching threats, and mineral deposits.',
    civilisationBonus: '+15% land use efficiency from underground expansion',
    flavourQuote: '"The surface is just the roof of our true home."',
    gradient: 'from-stone-950/80 to-slate-900',
    accent: 'text-stone-300',
    badgeBg: 'bg-stone-900/40 border-stone-700/40',
  },
  glow: {
    title: 'The Luminous Federation',
    trait: 'Enlightened',
    originStory: 'Bioluminescent hunters of the ocean depths, the glow-fish developed complex light-patterns for communication long before they developed language. Their societies are built on illumination — both literal and metaphorical. Darkness, to them, is simply a canvas.',
    uniqueAbility: 'Light-Language — complex information can be transmitted in seconds through bioluminescent patterns, enabling high-bandwidth communication across their colonies.',
    civilisationBonus: '+20% energy and happiness from luminous infrastructure',
    flavourQuote: '"In the deepest dark, we found that we ourselves could shine."',
    gradient: 'from-yellow-950/80 to-slate-900',
    accent: 'text-yellow-300',
    badgeBg: 'bg-yellow-900/40 border-yellow-700/40',
  },
  vine: {
    title: 'The Verdant Empire',
    trait: 'Symbiotic',
    originStory: 'Semi-sentient plant-creatures that spread across every surface they could reach, the vine-folk do not compete with other life — they incorporate it. Their bodies host entire ecosystems of fungi, bacteria, and small animals. They are not individuals; they are walking forests.',
    uniqueAbility: 'Root Network — their underground root systems connect across vast distances, allowing nutrient sharing, communication, and collective defence against threats.',
    civilisationBonus: '+15% biomass and biodiversity from symbiotic agriculture',
    flavourQuote: '"We do not rule the land. We are the land, thinking."',
    gradient: 'from-green-950/80 to-slate-900',
    accent: 'text-green-300',
    badgeBg: 'bg-green-900/40 border-green-700/40',
  },
  swarm: {
    title: 'The Swarm Collective',
    trait: 'Adaptive',
    originStory: 'Tiny organisms that achieved intelligence not individually but through emergent behaviour, the swarm is a living supercomputer. Billions of microscopic minds vote on every decision through chemical signals. They are democracy taken to its absolute extreme.',
    uniqueAbility: 'Distributed Resilience — no single point of failure exists. The swarm can lose 90% of its members and still function, adapting its behaviour to whatever numbers remain.',
    civilisationBonus: '+10% all stats during crises from adaptive swarm logic',
    flavourQuote: '"One of us is nothing. A billion of us is a mind."',
    gradient: 'from-orange-950/80 to-slate-900',
    accent: 'text-orange-300',
    badgeBg: 'bg-orange-900/40 border-orange-700/40',
  },
  reefback: {
    title: 'The Reefback Covenant',
    trait: 'Ecological',
    originStory: 'Massive reef-dwelling grazers that carry entire ecosystems on their backs, the reefbacks learned early that their survival depends on the health of everything around them. They became careful ecological planners, tending to coral gardens and fish populations with the patience of gardeners.',
    uniqueAbility: 'Ecosystem Engineering — they can cultivate and direct the growth of aquatic ecosystems, creating self-sustaining food webs and natural barriers.',
    civilisationBonus: '+20% conservation scoring from ecological planning',
    flavourQuote: '"We do not own the reef. We are its caretakers, and it is ours."',
    gradient: 'from-teal-950/80 to-slate-900',
    accent: 'text-teal-300',
    badgeBg: 'bg-teal-900/40 border-teal-700/40',
  },
  embermoth: {
    title: 'The Embermoth Signal-Seers',
    trait: 'Transformative',
    originStory: 'Heat-loving fliers drawn to light, ash, and electromagnetic fields, the embermoths evolved near volcanic vents and radioactive deposits. They see in spectra invisible to other life and communicate through subtle shifts in their own body heat.',
    uniqueAbility: 'Thermal Vision — they perceive the world through heat signatures, allowing them to detect hidden threats, energy sources, and atmospheric changes invisible to ordinary sight.',
    civilisationBonus: '+15% signal reach from thermal communication towers',
    flavourQuote: '"The universe speaks in heat and light. We are learning to listen."',
    gradient: 'from-red-950/80 to-slate-900',
    accent: 'text-red-300',
    badgeBg: 'bg-red-900/40 border-red-700/40',
  },
  glasscrawler: {
    title: 'The Glasscrawler Logic Weavers',
    trait: 'Precise',
    originStory: 'Mineral-shelled thinkers that favour stable land and crystalline chemistry, the glasscrawlers build their homes from secreted silica — transparent, beautiful, and incredibly strong. Their society values precision above all else; they do not approximate, they calculate.',
    uniqueAbility: 'Silicate Fabrication — they can grow complex glass structures with molecular precision, creating lenses, fibres, and crystalline computers from raw sand.',
    civilisationBonus: '+20% scan confidence from precision optics',
    flavourQuote: '"Chaos is just mathematics we have not solved yet."',
    gradient: 'from-cyan-950/80 to-slate-900',
    accent: 'text-cyan-300',
    badgeBg: 'bg-cyan-900/40 border-cyan-700/40',
  },
  stormray: {
    title: 'The Stormray Weather-Speakers',
    trait: 'Dynamic',
    originStory: 'Atmospheric gliders that ride weather fronts, the stormrays learn through pressure, sound, and electrical charge. Their language is a complex mixture of infrasound and bioelectric pulses. They do not fear storms — they dance with them.',
    uniqueAbility: 'Weather Prediction — their bodies sense barometric pressure, humidity, and ionisation changes hours before storms arrive, giving them perfect weather forecasting.',
    civilisationBonus: '+15% exploration during storms from weather mastery',
    flavourQuote: '"The storm is not our enemy. It is our teacher, and our dance partner."',
    gradient: 'from-blue-950/80 to-slate-900',
    accent: 'text-blue-300',
    badgeBg: 'bg-blue-900/40 border-blue-700/40',
  },
  tidewalker: {
    title: 'The Tidewalker Bridge Envoys',
    trait: 'Amphibious',
    originStory: 'Amphibious tool-users that bridge coasts, rivers, and early settlements, the tidewalkers are natural diplomats. Their existence depends on maintaining connections between land and water, and that philosophy extends to everything they build.',
    uniqueAbility: 'Aquatic Construction — they can build structures that function equally well above and below water, creating seamless ports, bridges, and aqueducts.',
    civilisationBonus: '+15% envoy success from diplomatic instinct',
    flavourQuote: '"Between two shores, there is always a bridge waiting to be built."',
    gradient: 'from-emerald-950/80 to-slate-900',
    accent: 'text-emerald-300',
    badgeBg: 'bg-emerald-900/40 border-emerald-700/40',
  },
  mossbeast: {
    title: 'The Mossbeast Grove Wardens',
    trait: 'Patient',
    originStory: 'Slow social megafauna that cultivate living shelter, the mossbeasts grow their homes from symbiotic moss and fungi. They do not hunt; they garden. They do not fight; they outwait. Their strategy is simple: plant, protect, and persist.',
    uniqueAbility: 'Living Architecture — their structures are alive, self-repairing, and self-improving over time. A mossbeast city gets stronger and greener with every passing century.',
    civilisationBonus: '+20% biodiversity recovery from living infrastructure',
    flavourQuote: '"We do not build for today. We plant for the thousand tomorrows."',
    gradient: 'from-lime-950/80 to-slate-900',
    accent: 'text-lime-300',
    badgeBg: 'bg-lime-900/40 border-lime-700/40',
  },
  duskfox: {
    title: 'The Duskfox Pattern Scouts',
    trait: 'Strategic',
    originStory: 'Adaptive land scouts with strong pattern memory, the duskfoxes navigate by remembering every tree, rock, and shadow they have ever seen. Their minds are libraries of terrain. They do not get lost — they get surprised by changes.',
    uniqueAbility: 'Territorial Memory — they remember every detail of terrain they traverse, building perfect mental maps that reveal hidden paths, resources, and ambush points.',
    civilisationBonus: '+15% diplomacy and scouting from pattern recognition',
    flavourQuote: '"The land tells stories to those who know how to read its wrinkles."',
    gradient: 'from-violet-950/80 to-slate-900',
    accent: 'text-violet-300',
    badgeBg: 'bg-violet-900/40 border-violet-700/40',
  },
  ironhorn: {
    title: 'The Ironhorn Shield Captains',
    trait: 'Resolute',
    originStory: 'Mineral-armoured plains builders, the ironhorns value defence, borders, and durable infrastructure above all. Their young are born with iron-rich plating that grows thicker with age. The oldest ironhorns are practically walking fortresses.',
    uniqueAbility: 'Magnetic Field Sense — their iron-rich bodies detect magnetic fields, giving them perfect directional sense and the ability to locate metal deposits from great distances.',
    civilisationBonus: '+20% defence scoring from fortified borders',
    flavourQuote: '"A wall is a promise to the future that the present will be defended."',
    gradient: 'from-stone-950/80 to-slate-900',
    accent: 'text-stone-300',
    badgeBg: 'bg-stone-900/40 border-stone-700/40',
  },
  skywhale: {
    title: 'The Skywhale Chorus Diplomats',
    trait: 'Expansive',
    originStory: 'Buoyant giants from thick atmospheres whose songs became the first long-distance language, the skywhales communicate across hundreds of kilometres through infrasonic song. Their civilisation is not built on proximity but on harmony.',
    uniqueAbility: 'Infrasonic Communication — their songs travel vast distances through atmosphere and ground, enabling coordination across continents without technology.',
    civilisationBonus: '+20% peaceful first contact from harmonic diplomacy',
    flavourQuote: '"Our voices crossed the sky long before our feet touched the ground."',
    gradient: 'from-sky-950/80 to-slate-900',
    accent: 'text-sky-300',
    badgeBg: 'bg-sky-900/40 border-sky-700/40',
  },
  frostmole: {
    title: 'The Frostmole Deep Planners',
    trait: 'Enduring',
    originStory: 'Cold-adapted tunnel engineers that store knowledge underground, the frostmoles plan on timescales that baffle warmer species. Their cities take centuries to build but last millennia. They do not rush. They prepare.',
    uniqueAbility: 'Deep Storage — they can preserve information, resources, and even living organisms in frozen vaults for centuries, creating perfect time capsules and emergency reserves.',
    civilisationBonus: '+15% long mission stability from deep planning',
    flavourQuote: '"The surface world changes every season. Down here, we remember everything."',
    gradient: 'from-slate-950/80 to-slate-900',
    accent: 'text-slate-300',
    badgeBg: 'bg-slate-800/60 border-slate-600/40',
  },
};

export default function EvolutionModal({ dominantCreature, onClose }: EvolutionModalProps) {
  const [visible, setVisible] = useState(false);
  const creatureKey = dominantCreature || 'blob';
  const lore = INTELLIGENT_LIFE_LORE[creatureKey] || INTELLIGENT_LIFE_LORE.blob;

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div
        className={`
          relative max-w-2xl w-full mx-4 rounded-2xl overflow-hidden
          bg-gradient-to-br ${lore.gradient}
          border border-slate-700/50 shadow-2xl
          transition-all duration-700 ease-out
          ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}
        `}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <Sparkles className={`w-4 h-4 ${lore.accent}`} />
            <span className={`text-xs font-semibold uppercase tracking-wider ${lore.accent}`}>Intelligent Life Emerges</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row gap-5">
            {/* Left: Creature image */}
            <div className="flex flex-col items-center md:w-48 shrink-0">
              <div className={`relative p-3 rounded-2xl ${lore.badgeBg} border`}>
                <CreatureAvatar type={creatureKey} variant="ambassador" className="w-28 h-28 md:w-32 md:h-32 rounded-xl" />
              </div>
              <div className="mt-3 text-center">
                <div className="text-lg font-bold text-white">{lore.title}</div>
                <div className={`text-xs uppercase tracking-wider mt-0.5 ${lore.accent}`}>{lore.trait}</div>
              </div>
            </div>

            {/* Right: Lore text */}
            <div className="flex-1 min-w-0 space-y-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Origin Story</div>
                <p className="text-sm text-slate-300 leading-relaxed">{lore.originStory}</p>
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Unique Ability</div>
                <p className="text-sm text-slate-300 leading-relaxed">{lore.uniqueAbility}</p>
              </div>

              <div className="p-3 rounded-lg bg-slate-950/50 border border-slate-700/40">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400/80 mb-1">Civilisation Bonus</div>
                <p className="text-sm text-emerald-100">{lore.civilisationBonus}</p>
              </div>

              <div className="flex items-start gap-2 pt-1">
                <Quote className={`w-4 h-4 mt-0.5 shrink-0 ${lore.accent} opacity-60`} />
                <p className={`text-sm italic leading-relaxed ${lore.accent} opacity-90`}>{lore.flavourQuote}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors text-sm"
            >
              Continue Building
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
